import { ArrowLeft, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import usePageTitle from "../utils/usePageTitle";

import { formatApiRequestError, get_process_details, get_processes_list } from "../api";
import type { Process, ProcessSummary } from "../api/types";
import { useDashboardDates } from "../components/dashboard/useDashboardDates";
import AddToVocabulary from "../components/processes/AddToVocabulary";
import ProcessCompareAction from "../components/processes/ProcessCompareAction";
import { ProcessCompareHeaderControls } from "../components/processes/ProcessComparePanel";
import ProcessDetailsSection, {
  ProcessDetailsDivider,
  sidebarDetailKind,
  useProcessMapSelection,
  useProcessDetails,
} from "../components/processes/ProcessDetailsSection";
import ProcessGraphCanvas from "../components/processes/ProcessGraphCanvas";
import ProcessSwitcher from "../components/processes/ProcessSwitcher";
import type { ProcessComparison } from "../components/processes/processComparison";
import { PROCESS_DIRECTORY_URL } from "../components/processes/processPreferences";
import {
  readComparisonLink,
  readProcessComparePair,
  readProcessTab,
  readProcessUrlScope,
  resolveComparisonLink,
  writeProcessTab,
} from "../components/processes/processViewUrl";
import { useProcessGraph } from "../components/processes/useProcessGraph";
import { type ScreenshotRef, useScreenshotCarousel } from "../components/screenshots";
import { useLogin } from "../reducers";
import { canSeeScreenshotsForUser } from "../utils/permissions";
import { getBrowserHash, hasInAppHistoryEntry } from "../utils/urlHash";
import {
  serializeUserFilterSelection,
  useUserFilterSelection,
} from "../utils/useUserFilterSelection";

import {
  type ProcessAction,
  type ResolvedProcessAction,
  resolveProcessAction,
} from "../components/processes/processAction";

// Every process, in the order the dashboard lists them, for the header's switcher. A failure
// leaves the switcher empty rather than breaking the page.
const useProcessList = (selectedUserIds: string[]) => {
  const [processes, setProcesses] = useState<ProcessSummary[]>([]);
  const userIdsKey = selectedUserIds.join(",");

  useEffect(() => {
    let cancelled = false;
    get_processes_list({ user_ids: userIdsKey ? userIdsKey.split(",") : [] })
      .then(list => {
        if (!cancelled) setProcesses(list);
      })
      .catch(() => {
        if (!cancelled) setProcesses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userIdsKey]);

  return processes;
};

// Loads the process and nothing else; the workspace below is what the URL, the scope and the
// panel all hang off, and it is remounted per process so none of that leaks across a switch.
const ProcessDetail = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const action = location.state?.processAction as ProcessAction | undefined;
  const [urlComparison, setUrlComparison] = useState<ProcessComparison>();
  const [loadedKey, setLoadedKey] = useState<string>();
  const [resolvedAction, setResolvedAction] = useState<ResolvedProcessAction>();
  const [retry, setRetry] = useState(0);
  const navigate = useNavigate();
  const { permissions } = useLogin();
  const peopleFilter = useUserFilterSelection("pu", permissions.processes.visualization);
  const dates = useDashboardDates();
  const filters = { people: peopleFilter, dates };
  const [process, setProcess] = useState<Process | null>(null);
  usePageTitle(
    (process && process.slug === slug ? process.name.trim() : "") || t("process_details"),
  );
  const [error, setError] = useState<string | null>(null);
  // Above the early returns, so it goes out in parallel with the process details.
  const processes = useProcessList(peopleFilter.selectedUserIds);

  useEffect(() => {
    setProcess(null);
    setResolvedAction(undefined);
    setError(null);
    if (!slug) return;
    let cancelled = false;
    const hash = getBrowserHash();
    const comparisonLink = readComparisonLink(hash);
    const explicitScope = readProcessUrlScope(hash) || readProcessComparePair(hash);
    get_process_details(slug)
      .then(async p => {
        const comparison = comparisonLink
          ? await resolveComparisonLink(comparisonLink, p, get_process_details)
          : undefined;
        const resolved =
          action && !explicitScope && !comparisonLink
            ? await resolveProcessAction(p, action)
            : undefined;
        if (
          resolved &&
          (!resolved.ids.length || (resolved.comparison && !resolved.comparison.b.runs.length))
        )
          throw new Error(t("askPanel.no_matching_cases"));
        if (!cancelled) {
          setLoadedKey(location.key);
          setUrlComparison(comparison);
          setResolvedAction(resolved);
          setError(null);
          setProcess(p);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setProcess(null);
          setError(formatApiRequestError(err, t));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, t, action, retry, location.key]);

  if (error) {
    return (
      <div className="px-6 pb-6 pt-7">
        <button
          type="button"
          onClick={() => (hasInAppHistoryEntry() ? navigate(-1) : navigate(PROCESS_DIRECTORY_URL))}
          aria-label={t("back_to_previous_page")}
          title={t("back_to_previous_page")}
          className="mb-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-text-dim transition hover:bg-surface-strong hover:text-text"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-strong">
          {t("failed_to_load_process")}: {error}
        </div>
        <button
          type="button"
          className="mt-3 text-sm text-info"
          onClick={() => setRetry(value => value + 1)}
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  if (!process || loadedKey !== location.key || peopleFilter.loadingUsers) {
    return <div className="px-6 pb-6 pt-7 text-sm text-text-dim">{t("loading_ellipsis")}</div>;
  }

  return (
    <ProcessWorkspace
      key={`${process.slug}:${location.key}`}
      process={process}
      filters={filters}
      processes={processes}
      initialComparison={urlComparison}
      initialAction={resolvedAction}
    />
  );
};

// One scope shared by the process map and side panel.
const ProcessWorkspace = ({
  process,
  filters,
  processes,
  initialAction,
  initialComparison,
}: {
  process: Process;
  initialAction?: ResolvedProcessAction;
  initialComparison?: ProcessComparison;
  filters: {
    people: ReturnType<typeof useUserFilterSelection>;
    dates: ReturnType<typeof useDashboardDates>;
  };
  /** Every process, for the header's switcher. */
  processes: ProcessSummary[];
}) => {
  const { t } = useTranslation();
  const { user, permissions } = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedUserIds = filters.people.selectedUserIds;
  const userIdList = selectedUserIds.join(",");

  const people = useMemo(
    () => Object.fromEntries(filters.people.userOptions.map(option => [option.id, option.label])),
    [filters.people.userOptions],
  );
  const inheritedScope = useMemo(
    () => ({
      // Everyone visible is no people filter at all, as the page's own people filter has it, so
      // picking everyone there matches this rather than overriding it.
      userIds:
        serializeUserFilterSelection(selectedUserIds, filters.people.userOptions) === "all"
          ? []
          : selectedUserIds,
      noUsers: selectedUserIds.length === 0,
      from: filters.dates.from || null,
      to: filters.dates.to || null,
    }),
    [selectedUserIds, filters.people.userOptions, filters.dates.from, filters.dates.to],
  );
  const state = useProcessGraph({
    process,
    inheritedScope,
    userIdList,
    initialScope: initialAction?.scope,
    initialComparison: initialComparison ?? initialAction?.comparison,
    onEnterCompare: hash =>
      navigate(
        { pathname: location.pathname, search: location.search, hash },
        {
          state: {
            ...location.state,
            processAction: undefined,
            processCompareReturn: location.key,
          },
        },
      ),
    onExitCompare: () => {
      if (!location.state?.processCompareReturn || !hasInAppHistoryEntry()) return false;
      navigate(-1);
      return true;
    },
  });
  const sidebar = useProcessDetails({
    initialInspectorOpen: !!state.selection,
    initialInstanceOpen: !!state.highlightedInstanceId,
    initialTab: readProcessTab(),
  });
  useEffect(() => writeProcessTab(sidebar.tab), [sidebar.tab]);
  const {
    canvasRef,
    transitionSelection,
    revealSelection,
    openSelection,
    clearSelection,
    clearStepSelection,
    backFromStep,
    hasPreviousStep,
    clearTransitionSelection,
    openInstanceSelection,
    closeInstanceSelection,
    closeDetails,
    leaveCompare,
    selectNode,
    selectTransition,
    hoverBranch,
    signalStepBoundary,
    selectCompareStep,
    instanceOrigin,
  } = useProcessMapSelection(state, sidebar);
  const { showScreenshots, carousel } = useScreenshotCarousel();
  const showAllowedScreenshots = (screenshots: ScreenshotRef[]) => {
    const allowed = screenshots.filter(
      screenshot =>
        !screenshot.userId || canSeeScreenshotsForUser(permissions, user, screenshot.userId),
    );
    if (allowed.length > 0) showScreenshots(allowed);
  };

  // The back row names the open evidence, and its back action returns to the detail a case was
  // opened from, or to the tab under it — dropping the selection with it.
  const detailKind = sidebarDetailKind(state, sidebar, transitionSelection);
  const backToTab = t(`processPanel.backTo_${sidebar.tab}`);
  const sidebarDetail =
    detailKind === "instance"
      ? {
          title: t("instance_view"),
          closeHint:
            instanceOrigin === "overview"
              ? // Opened from a tab's own list: no detail was uncovered by it, so the way out is
                // the tab rather than the step the timeline had to select.
                backToTab
              : transitionSelection
                ? t("back_to_transition_details")
                : t("back_to_step_details"),
          onClose: closeInstanceSelection,
        }
      : detailKind === "transition"
        ? {
            title: t("transition_details"),
            closeHint: backToTab,
            onClose: clearTransitionSelection,
          }
        : detailKind === "step"
          ? {
              title: t("step_details"),
              closeHint: hasPreviousStep ? t("back_to_step_details") : backToTab,
              onClose: backFromStep,
            }
          : null;

  // Switching to a sibling process drops the hash: the node and instance picks in it belong to
  // the process being left, and would resolve to nothing (or worse, something) in the next one.
  const navigateToProcess = (target: ProcessSummary) => {
    navigate({
      pathname: `/processes/${encodeURIComponent(target.slug)}/`,
      search: "",
      hash: "",
    });
  };

  const processHeader = (
    <header
      data-tour="process-tabs"
      className={`flex shrink-0 items-center gap-2 border-b border-outline ${
        state.comparePair ? "flex-wrap px-4 py-2.5" : "h-10 min-w-0 px-3"
      }`}
    >
      <div className={state.comparePair ? "min-w-0" : "min-w-0 flex-1"}>
        <ProcessSwitcher
          process={process}
          processes={processes}
          onSelect={navigateToProcess}
          viewerId={user.id}
          people={people}
        />
      </div>
      {!state.comparePair && (
        <div className="shrink-0">
          <ProcessCompareAction state={state} processes={processes} people={people} />
        </div>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {state.comparePair ? (
          <>
            <ProcessCompareHeaderControls state={state} />
            {state.comparison && (
              <ProcessCompareAction state={state} processes={processes} people={people} />
            )}
            <button
              type="button"
              onClick={leaveCompare}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 text-xs font-medium text-text-mid transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t("exit_compare")}
            </button>
          </>
        ) : (
          <AddToVocabulary
            inActionsMenu
            processName={process.name}
            stepNames={process.nodes.map(node => node.name)}
          />
        )}
      </div>
    </header>
  );

  return (
    // The page owns the viewport and never scrolls itself; the map pans and the panels scroll.
    <div className="app-scroll-fill pd-theme flex h-full min-h-0 flex-col overflow-hidden bg-surface">
      {state.comparePair && processHeader}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface">
          {!state.comparePair && processHeader}
          <div data-tour="process-graph" className="flex min-h-0 flex-1 flex-col">
            <ProcessGraphCanvas
              ref={canvasRef}
              state={state}
              expanded={sidebar.collapsed}
              onToggleExpanded={() => sidebar.setCollapsed(value => !value)}
              selectedTransition={transitionSelection}
              peopleOptions={filters.people.userOptions}
              onSelect={revealSelection}
              onOpen={openSelection}
              onClearSelection={clearSelection}
            />
          </div>
        </div>
        {!sidebar.collapsed && <ProcessDetailsDivider controller={sidebar} />}
        <ProcessDetailsSection
          state={state}
          controller={sidebar}
          transitionSelection={transitionSelection}
          detail={sidebarDetail}
          onCloseDetails={closeDetails}
          onSelectNode={selectNode}
          onSelectTransition={selectTransition}
          onReachStepBoundary={signalStepBoundary}
          onSelectCompareStep={selectCompareStep}
          onShowScreenshots={showAllowedScreenshots}
          onClearStepSelection={clearStepSelection}
          onOpenInstance={openInstanceSelection}
          onClearInstanceSelection={closeInstanceSelection}
          onClearTransitionSelection={clearTransitionSelection}
          onHoverBranch={hoverBranch}
        />
        {carousel}
      </div>
    </div>
  );
};

export default ProcessDetail;
